import { useState } from 'react';
import { MapPin, Phone, Mail, Clock, Send } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from '../hooks/use-toast';
import { company } from '../mock';
import { submitContact } from '../lib/api';

export default function ContactSection({ compact = false }) {
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({ title: 'Missing fields', description: 'Please fill all required fields.' });
      return;
    }
    setLoading(true);
    try {
      await submitContact(form);
      toast({
        title: 'Enquiry sent!',
        description: 'Our team will get back to you within 24 hours.',
      });
      setForm({ name: '', email: '', phone: '', message: '' });
    } catch (err) {
      toast({
        title: 'Something went wrong',
        description: 'Please try again or call us directly.',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className={`bg-white ${compact ? 'py-16' : 'py-24 lg:py-32'}`}>
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-2 gap-14">
          <div>
            <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-amber-600 mb-4">
              — Let&rsquo;s Connect
            </div>
            <h2 className="font-display text-4xl lg:text-5xl font-semibold text-[#0b0b0d] leading-[1.05]">
              Ready to place an order or need a quote?
            </h2>
            <p className="mt-5 text-neutral-600 leading-relaxed">
              Reach out to our team for pricing, product samples, technical guidance, or bulk
              orders. We respond within 24 hours.
            </p>

            <div className="mt-10 space-y-5">
              <InfoRow icon={MapPin} title="Head Office & Factory" value={company.address} />
              <InfoRow icon={Phone} title="Call Us" value={company.phone} href={`tel:${company.phone}`} />
              <InfoRow icon={Mail} title="Email" value={company.email} href={`mailto:${company.email}`} />
              <InfoRow icon={Clock} title="Hours" value="Mon–Sat, 9:00 AM – 6:30 PM IST" />
            </div>
          </div>

          <form
            onSubmit={submit}
            className="rounded-2xl border border-neutral-200 bg-neutral-50/60 p-8 lg:p-10 shadow-sm"
          >
            <h3 className="font-display text-xl font-semibold text-[#0b0b0d]">Submit your inquiry</h3>
            <p className="text-sm text-neutral-500 mt-1">We&rsquo;ll route your message to the right team.</p>

            <div className="mt-6 space-y-4">
              <div>
                <label className="text-xs font-medium text-neutral-700 uppercase tracking-wider">Full Name *</label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-1.5 h-11 bg-white"
                  placeholder="Your name"
                />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-neutral-700 uppercase tracking-wider">Email *</label>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="mt-1.5 h-11 bg-white"
                    placeholder="you@company.com"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-neutral-700 uppercase tracking-wider">Phone</label>
                  <Input
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="mt-1.5 h-11 bg-white"
                    placeholder="+91 —"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-neutral-700 uppercase tracking-wider">Message *</label>
                <Textarea
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="mt-1.5 min-h-32 bg-white"
                  placeholder="Tell us about your requirement..."
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="btn-primary w-full h-12 bg-[#0b0b0d] hover:bg-[#0b0b0d] text-white font-semibold rounded-md"
              >
                {loading ? 'Sending...' : (<>Send Message <Send className="ml-2 h-4 w-4" /></>)}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}

function InfoRow({ icon: Icon, title, value, href }) {
  const content = (
    <div className="flex items-start gap-4 group">
      <div className="h-11 w-11 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center shrink-0 group-hover:bg-amber-500 group-hover:border-amber-500 transition-colors">
        <Icon className="h-5 w-5 text-amber-600 group-hover:text-white transition-colors" />
      </div>
      <div>
        <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-neutral-500">
          {title}
        </div>
        <div className="mt-0.5 text-[#0b0b0d] font-medium">{value}</div>
      </div>
    </div>
  );
  return href ? (
    <a href={href} className="block hover:opacity-80 transition-opacity">{content}</a>
  ) : content;
}
